import { useApplicantsSearch } from "@/queries/recruit/use-applicants.query.ts";
import { ApiAnalyticsRecruitingFilter } from "@/types/analytics/analytics.recruiting.types.ts";
import {
  ApiApplicantFilter,
  ApiApplicantShort,
  ApplicantFilterDefault,
} from "@/types/recruit/applicant.types.ts";
import { Avatar, LinkTo, PaginationBlock, Stack, Table, Title } from "@skailer-com/uikit";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

type RecruitingClosingTimeReportHiredTableProps = {
  analytics_filter?: ApiAnalyticsRecruitingFilter;
  hired_applicants_ids?: number[];
};

export const RecruitingClosingTimeReportHiredTable = ({
  analytics_filter,
  hired_applicants_ids,
}: RecruitingClosingTimeReportHiredTableProps) => {
  const { t } = useTranslation();

  const [pageNumber, setPageNumber] = useState(1);

  const filter = useMemo<ApiApplicantFilter>(
    () => ({
      page_number: pageNumber,
      sorting: ApplicantFilterDefault.sorting,
      applicant_ids: hired_applicants_ids,
      job_ids: analytics_filter?.job_ids,
    }),
    [pageNumber, hired_applicants_ids, analytics_filter?.job_ids]
  );

  const hasHiredApplicants = (hired_applicants_ids?.length ?? 0) > 0;

  const { data: applicants, isLoading } = useApplicantsSearch(filter, hasHiredApplicants);

  const columns = useMemo<ColumnDef<ApiApplicantShort>[]>(() => {
    return [
      {
        header: t("ats:job"),
        cell: (row) => (
          <>
            {row.row.original.candidates.length > 0 && (
              <Stack direction={"horizontal"} gap={"md"} items={"center"}>
                <Avatar
                  size={"md"}
                  url={row.row.original.photo_url}
                  acronym={`${row.row.original.first_name} ${row.row.original.last_name}`}
                />
                <Stack>
                  <LinkTo
                    to={`/recruitment/candidates/${row.row.original.id}`}
                    variant={"secondary"}
                    target={"_blank"}
                    className={"text-title overflow-hidden text-ellipsis whitespace-nowrap"}
                  >
                    {`${row.row.original.first_name} ${row.row.original.last_name}`}
                  </LinkTo>
                </Stack>
              </Stack>
            )}
          </>
        ),
        accessorKey: "title",
      },
    ];
  }, [applicants]);
  return (
    <>
      <Title header={t("ats:hired_candidates")} paddingTop paddingBottom />
      <Table data={applicants?.items || []} columns={columns} showSkeleton={isLoading} />
      <PaginationBlock
        metadata={applicants?.metadata}
        onPageChange={(page) => {
          setPageNumber(page);
        }}
      />
    </>
  );
};
